import { useEffect, useMemo, useRef } from 'react';
import { getMessages } from '@connect-x-app/hybrid-data-access';
import { DEFAULT_PAGE_SIZE, useInternetConnection } from '@connect-x-app/shared-utils';
import { ChatDataResult } from '@connect-x-app/shared-view-models';

import { useMessageChunksForChannel, useMessageSessionStore, useMessageStore } from '../../stores';
import { useGetMessages } from '../useGetMessages';
import { useChannels } from './useChannels';
import { useFlightRooms } from './useFlightRooms';

type ChatDataProps = {
  focusedMessageId?: string | null;
  isChannelPreviewMode?: boolean;
};

export const useChatData = (props?: ChatDataProps): ChatDataResult => {
  const { selectedFlightRoom } = useFlightRooms();
  const { selectedChannel } = useChannels();
  const flightroomId = selectedFlightRoom?.id;
  const channelId = selectedChannel?.id;
  const firstUnreadMessageId = selectedChannel?.firstUnreadMessage;

  const { setHasLoadedLastPage } = useMessageSessionStore();

  const {
    fetchNextPage: fetchNewerMessages,
    fetchPreviousPage: fetchOlderMessages,
    hasLoadedLastPage,
    hasNextPage: hasNewerMessages,
    hasPreviousPage: hasOlderMessages,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    messages: newlyFetchedMessages,
  } = useGetMessages(
    flightroomId,
    channelId,
    props?.focusedMessageId ?? (props?.isChannelPreviewMode ? undefined : firstUnreadMessageId) ?? undefined,
  );

  const isFillingGaps = useRef(false);
  const { isInternetConnectionOnline } = useInternetConnection();

  const addMessages = useMessageStore(state => state.addMessages);
  const messageChunksForChannel = useMessageChunksForChannel(channelId);

  useEffect(() => {
    setHasLoadedLastPage(hasLoadedLastPage);
  }, [hasLoadedLastPage, setHasLoadedLastPage]);

  useEffect(() => {
    if (newlyFetchedMessages && channelId) {
      addMessages({ channelId, messages: newlyFetchedMessages });
    }
  }, [addMessages, channelId, newlyFetchedMessages]);

  useEffect(() => {
    if (flightroomId && channelId && isInternetConnectionOnline && messageChunksForChannel.length > 1 && !isFillingGaps.current) {
      isFillingGaps.current = true;
      getMessages(
        flightroomId,
        channelId,
        { limit: DEFAULT_PAGE_SIZE },
        Array.from(messageChunksForChannel[0].values()).pop()?.id,
        'before',
        true,
      )
        .then(response => {
          addMessages({ channelId, messages: response.items ?? [] });
        })
        .finally(() => {
          isFillingGaps.current = false;
        });
    }
  }, [addMessages, channelId, flightroomId, isInternetConnectionOnline, messageChunksForChannel]);

  const chat = useMemo(() => {
    return messageChunksForChannel.map(chunk => Array.from(chunk.values())).flat();
  }, [messageChunksForChannel]);

  return {
    chat,
    fetchNewerMessages,
    fetchOlderMessages,
    firstUnreadMessageId,
    hasLoadedLastPage,
    hasNewerMessages,
    hasOlderMessages,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
  };
};
